import { useEffect, useRef, type ReactNode } from "react";

// 3D interactive nebula. Stars are scattered uniformly inside a sphere.
// Children sit between the back/front canvas layers so the nebula rotates around the page center.

type Star = {
  x: number; y: number; z: number;
  group: number;
  baseR: number;
  anchor: boolean;
};

const STAR_TINT = "255,255,255";
const HOVER_TINT = "255,210,170";

export function Nebula({
  groups = 7,
  perGroup = 70,
  anchorsPerGroup = 2,
  className = "",
  children,
}: {
  groups?: number;
  perGroup?: number;
  anchorsPerGroup?: number;
  className?: string;
  children?: ReactNode;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLCanvasElement>(null);
  const frontRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0, y: 0, active: false });

  useEffect(() => {
    const wrap = wrapRef.current!;
    const back = backRef.current!;
    const front = frontRef.current!;
    const bctx = back.getContext("2d")!;
    const fctx = front.getContext("2d")!;
    let raf = 0;
    let w = 0, h = 0, dpr = 1;

    const RING_R = 1;
    const RING_THICK = 0.25;
    const sampleRR = () => RING_THICK * Math.pow(Math.random(), 1.6);
    const ringPoint = () => {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI * 2;
      const rr = sampleRR();
      return {
        x: (RING_R + rr * Math.cos(phi)) * Math.cos(theta),
        z: (RING_R + rr * Math.cos(phi)) * Math.sin(theta),
        y: rr * Math.sin(phi),
      };
    };

    const stars: Star[] = [];
    for (let g = 0; g < groups; g++) {
      const baseTheta = (g / groups) * Math.PI * 2;
      for (let i = 0; i < perGroup; i++) {
        const isAnchor = i < anchorsPerGroup;
        const theta = isAnchor
          ? ((g * anchorsPerGroup + i) / (groups * anchorsPerGroup)) * Math.PI * 2
            + (Math.random() - 0.5) * 0.15
          : baseTheta + (Math.random() - 0.5) * 1.6;
        const phi = Math.random() * Math.PI * 2;
        const rr = isAnchor ? RING_THICK * 0.15 * Math.random() : sampleRR();
        const clustered = {
          x: (RING_R + rr * Math.cos(phi)) * Math.cos(theta),
          z: (RING_R + rr * Math.cos(phi)) * Math.sin(theta),
          y: rr * Math.sin(phi),
        };
        const p = isAnchor || Math.random() < 0.7 ? clustered : ringPoint();
        stars.push({
          x: p.x, y: p.y, z: p.z,
          group: g,
          baseR: isAnchor ? Math.random() * 1.8 + 2.6 : Math.random() * 1.0 + 0.35,
          anchor: isAnchor,
        });
      }
    }

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = rect.width; h = rect.height;
      for (const c of [back, front]) {
        c.width = w * dpr;
        c.height = h * dpr;
      }
      bctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      fctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const onMove = (e: MouseEvent) => {
      const rect = wrap.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
      mouseRef.current.active = true;
    };
    const onLeave = () => { mouseRef.current.active = false; };
    wrap.addEventListener("mousemove", onMove);
    wrap.addEventListener("mouseleave", onLeave);

    let t = 0;
    const CENTER_X = 0.5;
    const CENTER_Y = 0.5;
    const TILT_Z = -13 * Math.PI / 180;
    const TILT_X = 15 * Math.PI / 180;
    const cosTZ = Math.cos(TILT_Z), sinTZ = Math.sin(TILT_Z);
    const cosTX = Math.cos(TILT_X), sinTX = Math.sin(TILT_X);

    const project = (x: number, _y: number, z: number) => {
      const focal = 700;
      const radius = Math.min(w, h) * 0.5;
      const scale = focal / (focal + z * 240);
      return {
        sx: w * CENTER_X + x * radius * scale,
        sy: h * CENTER_Y + _y * radius * scale,
        scale,
      };
    };

    const render = () => {
      t += 0.0008;
      bctx.clearRect(0, 0, w, h);
      fctx.clearRect(0, 0, w, h);
      bctx.globalCompositeOperation = "lighter";
      fctx.globalCompositeOperation = "lighter";

      const mx = mouseRef.current.active ? (mouseRef.current.x / w - 0.5) : 0;
      const my = mouseRef.current.active ? (mouseRef.current.y / h - 0.5) : 0;
      const ay = t + mx * 0.3;
      const ax = my * 0.2;
      const cosY = Math.cos(ay), sinY = Math.sin(ay);
      const cosX = Math.cos(ax), sinX = Math.sin(ax);

      const transformed = stars.map((s) => {
        const x1 = s.x * cosY - s.z * sinY;
        const z1 = s.x * sinY + s.z * cosY;
        const y1 = s.y;
        const x2 = x1 * cosTZ - y1 * sinTZ;
        const y2a = x1 * sinTZ + y1 * cosTZ;
        const y2 = y2a * cosTX - z1 * sinTX;
        const z2 = y2a * sinTX + z1 * cosTX;
        const y3 = y2 * cosX - z2 * sinX;
        const z3 = y2 * sinX + z2 * cosX;
        const p = project(x2, y3, z3);
        return { s, z: z3, sx: p.sx, sy: p.sy, scale: p.scale };
      });

      let hoveredGroup = -1;
      if (mouseRef.current.active) {
        let best = Infinity;
        for (const p of transformed) {
          if (!p.s.anchor) continue;
          const hitR = 44 * (0.6 + p.scale * 0.9);
          const d = Math.hypot(p.sx - mouseRef.current.x, p.sy - mouseRef.current.y);
          if (d < hitR && d < best) { best = d; hoveredGroup = p.s.group; }
        }
      }

      transformed.sort((a, b) => b.z - a.z);

      if (hoveredGroup >= 0) {
        const members = transformed.filter((p) => p.s.group === hoveredGroup);
        fctx.lineWidth = 0.5;
        for (let i = 0; i < members.length; i++) {
          const a = members[i];
          const dists = members
            .map((b, j) => ({ j, d: Math.hypot(a.sx - b.sx, a.sy - b.sy) }))
            .filter((x) => x.j !== i)
            .sort((x, y) => x.d - y.d)
            .slice(0, 2);
          for (const { j, d } of dists) {
            if (d > 320) continue;
            const b = members[j];
            const alpha = (1 - d / 320) * 0.4;
            fctx.strokeStyle = `rgba(${HOVER_TINT},${alpha})`;
            fctx.beginPath();
            fctx.moveTo(a.sx, a.sy);
            fctx.lineTo(b.sx, b.sy);
            fctx.stroke();
          }
        }
      }

      transformed.forEach((p) => {
        const isHover = hoveredGroup === p.s.group;
        const tint = isHover ? HOVER_TINT : STAR_TINT;
        const r = p.s.baseR * p.scale * (isHover ? 1.7 : 1);
        const baseAlpha = p.s.anchor ? 0.98 : 0.56;
        const alpha = (isHover ? 1 : baseAlpha) * (0.55 + p.scale * 0.55);
        const ctx = p.z <= 0 ? fctx : bctx;

        const glowR = r * (p.s.anchor ? 30 : 18) * (isHover ? 1.35 : 1);
        const glow = ctx.createRadialGradient(p.sx, p.sy, 0, p.sx, p.sy, glowR);
        const innerA = alpha * (p.s.anchor ? 0.6 : 0.48);
        glow.addColorStop(0, `rgba(${tint},${innerA})`);
        glow.addColorStop(0.12, `rgba(${tint},${innerA * 0.55})`);
        glow.addColorStop(0.28, `rgba(${tint},${innerA * 0.22})`);
        glow.addColorStop(0.5, `rgba(${tint},${innerA * 0.07})`);
        glow.addColorStop(0.75, `rgba(${tint},${innerA * 0.02})`);
        glow.addColorStop(1, `rgba(${tint},0)`);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, glowR, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(${tint},${Math.min(1, alpha + 0.3)})`;
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, r, 0, Math.PI * 2);
        ctx.fill();
      });

      bctx.globalCompositeOperation = "source-over";
      fctx.globalCompositeOperation = "source-over";
      raf = requestAnimationFrame(render);
    };
    render();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      wrap.removeEventListener("mousemove", onMove);
      wrap.removeEventListener("mouseleave", onLeave);
    };
  }, [groups, perGroup, anchorsPerGroup]);

  return (
    <div ref={wrapRef} className={`absolute inset-0 ${className}`}>
      <canvas ref={backRef} className="absolute inset-0 w-full h-full" style={{ display: "block" }} />
      {children ? <div className="relative z-10 w-full h-full">{children}</div> : null}
      <canvas ref={frontRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ display: "block", zIndex: 20 }} />
    </div>
  );
}
